import { CloseIcon } from 'assets/icons/CloseIcon'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { Button } from 'components/ui/Button/Button'
import { ISelectValue } from 'constants/globalTypes'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import {
    IInventoryAdjustmentCreateState,
    IInventoryAdjustmentModalProps,
} from 'types/inventoryControl.interface'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { ADJUSTMENT_REASON_OPTIONS, CHARGE_KIND_OPTIONS } from '../Shared/inventoryFormatters'

export const InventoryAdjustmentModal = ({ closeModal, onCreate }: IInventoryAdjustmentModalProps) => {
    const [state, setState] = useState<IInventoryAdjustmentCreateState>({
        reason: null,
        location: null,
        item: null,
        quantity: null,
        chargeToKind: null,
        chargeToLabel: '',
        note: '',
    })
    useFocusFirstInput()
    useModalClose(closeModal)

    const handleSelect = (value: ISelectValue, name: string) => {
        setState((prev) => ({ ...prev, [name]: value }))
    }

    const invalid =
        !state.reason ||
        !state.location?.value ||
        !state.item?.value ||
        !state.quantity ||
        !state.chargeToKind ||
        !state.chargeToLabel.trim()

    const handleSave = () => {
        if (invalid) return
        onCreate(state)
        closeModal()
    }

    return <Modal black>
        <Flex column between center>
            <Flex column gap='1rem' width='100%'>
                <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
                    <TitleText weight='bold'>New adjustment</TitleText>
                    <CursorPointer onClick={closeModal}>
                        <CloseIcon />
                    </CursorPointer>
                </Flex>
                <HonestSelectInput
                    nomarg
                    labelText='Reason'
                    name='reason'
                    options={ADJUSTMENT_REASON_OPTIONS}
                    defaultValue={state.reason?.value}
                    onChange={handleSelect}
                    required
                    invalid={!state.reason}
                />
                <HonestSelectAsyncInput
                    nomarg
                    labelText='Location'
                    name='location'
                    link='locations'
                    defaultValue={state.location}
                    parserFunction={parseLocationRegistryOptions}
                    onChange={(value: ISelectValue) => setState((prev) => ({ ...prev, location: value }))}
                    required
                    invalid={!state.location?.value}
                />
                <Flex gap='1rem'>
                    <HonestSelectAsyncInput
                        nomarg
                        labelText='Item'
                        name='item'
                        link='inventory/item'
                        defaultValue={state.item}
                        parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                        onChange={(value: ISelectValue) => setState((prev) => ({ ...prev, item: value }))}
                        required
                        invalid={!state.item?.value}
                    />
                    <Input
                        placeholder='-4'
                        labelText='Qty delta'
                        value={state.quantity}
                        type='number'
                        name='quantity'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setState((prev) => ({ ...prev, quantity: e.target.valueAsNumber }))
                        }
                        nomarg
                        required
                        invalid={!state.quantity}
                    />
                </Flex>
                <Flex gap='1rem'>
                    <HonestSelectInput
                        nomarg
                        labelText='Charge to'
                        name='chargeToKind'
                        options={CHARGE_KIND_OPTIONS}
                        defaultValue={state.chargeToKind?.value}
                        onChange={handleSelect}
                        required
                        invalid={!state.chargeToKind}
                    />
                    <Input
                        placeholder='Warehouse ops'
                        labelText='Charge to (label)'
                        value={state.chargeToLabel}
                        type='text'
                        name='chargeToLabel'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setState((prev) => ({ ...prev, chargeToLabel: e.target.value }))
                        }
                        nomarg
                        required
                        invalid={!state.chargeToLabel.trim()}
                    />
                </Flex>
                <Input
                    type='textarea'
                    height='100px'
                    placeholder='Enter note'
                    labelText='Note'
                    value={state.note}
                    name='note'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setState((prev) => ({ ...prev, note: e.target.value }))
                    }
                    nomarg
                />
            </Flex>
            <Button style={{ marginTop: '1rem' }} disabled={invalid} variant='primary' onClick={handleSave}>
                Save
            </Button>
        </Flex>
    </Modal>
}
